import { z } from 'zod';
import { handoffReasonSchema, leadContactTypeSchema, leadDetailsSchema, } from '../../conversations.js';
import { industrySchema } from '../../industry.js';
/**
 * The widget API, v1 (`patterns/api-conventions.md`, 7; `patterns/auth-patterns.md`,
 * 5). Every path names the website by its public website key. Requests come
 * from the customer's website, so the API checks their `Origin`; a
 * conversation is reached only with the token it was created with, sent in
 * `X-Conversation-Token`.
 *
 * The widget imports only the types from here: zod never reaches the bundle
 * (`patterns/frontend-conventions.md`, 2).
 */
export const WIDGET_API_BASE = '/api/widget/v1';
export const CONVERSATION_TOKEN_HEADER = 'X-Conversation-Token';
/** The longest visitor message the API accepts. */
export const MAX_VISITOR_MESSAGE_LENGTH = 2000;
const websiteParams = z.object({ websiteKey: z.string().min(1).max(64) });
const conversationParams = websiteParams.extend({ conversationId: z.uuid() });
// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------
/** A social profile the website links to, such as its Instagram page. */
export const widgetSocialLinkSchema = z.object({
    /** The network's name as shown: "Instagram", "LinkedIn". */
    network: z.string(),
    url: z.string(),
});
/**
 * Every way to contact the business that its knowledge publishes
 * (`product/business-rules.md`, Handoff); each is `null` (or empty) when it
 * publishes none.
 */
export const widgetContactDetailsSchema = z.object({
    phoneNumber: z.string().nullable(),
    contactFormUrl: z.string().nullable(),
    email: z.string().nullable(),
    /** A `wa.me` or `api.whatsapp.com` link. */
    whatsappUrl: z.string().nullable(),
    socialLinks: z.array(widgetSocialLinkSchema),
});
export const widgetSettingsSchema = z.object({
    websiteName: z.string(),
    /** `null` means the widget's neutral default accent. */
    accentColour: z.string().nullable(),
    industry: industrySchema,
    /** The visitor notice, always visible under the title (G11). */
    visitorNotice: z.string(),
    greeting: z.string(),
    /** Up to four questions the visitor can tap instead of typing. */
    suggestedQuestions: z.array(z.string()),
    /** The website's direct contact route, shown when the chat is rate limited. */
    contact: widgetContactDetailsSchema,
});
export const widgetSourceSchema = z.object({
    title: z.string(),
    url: z.string(),
    /** The sentence the answer relies on: opening the source scrolls to it and highlights it. */
    quote: z.string().nullable(),
    /** The heading that sentence sits under on the page. */
    section: z.string().nullable(),
});
export const widgetMessageSchema = z.object({
    id: z.uuid(),
    /** `agent`: a person on the website's team, once they took the chat over. */
    role: z.enum(['greeting', 'visitor', 'assistant', 'agent']),
    content: z.string(),
    /** Assistant messages only. */
    outcome: z.enum(['answered', 'handoff']).nullable(),
    handoffReason: handoffReasonSchema.nullable(),
    /** The pages an answer was based on (G8). Empty when none. */
    sources: z.array(widgetSourceSchema),
    /** For a handoff: the contact details to show (empty when the knowledge has none). */
    contact: widgetContactDetailsSchema.nullable(),
    /** Agent messages only: the agent's first name, or `null` when they gave none. */
    agentName: z.string().nullable(),
    /**
     * The assistant invites the visitor to leave their details: the widget shows
     * the lead form under it, until the visitor has sent it.
     */
    offersLeadForm: z.boolean(),
    /**
     * With `offersLeadForm`: the form is open at once (the visitor asked to be
     * contacted). Otherwise the widget shows a small button that opens it.
     */
    leadFormOpen: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const widgetConversationSchema = z.object({
    conversationId: z.uuid(),
    /** `true` once the conversation was ended; the widget then starts a new one. */
    ended: z.boolean(),
    /** `true` once a person took the chat over: replies come from them, not the assistant. */
    live: z.boolean(),
    /** `true` once the visitor has left their details in the lead form: it isn't shown again. */
    leadDetailsSent: z.boolean(),
    messages: z.array(widgetMessageSchema),
});
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
export const getWidgetSettings = {
    method: 'GET',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/settings`,
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: widgetSettingsSchema,
};
export const startedConversationSchema = widgetConversationSchema.extend({
    /** Shown once; the API keeps only its hash. */
    conversationToken: z.string(),
});
/**
 * Starts a conversation: the API makes a random conversation id and token and
 * stores the greeting as its first message.
 */
export const startConversation = {
    method: 'POST',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/conversations`,
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: startedConversationSchema,
};
/**
 * Needs `X-Conversation-Token`. The widget reads it when the conversation's
 * event stream says it changed (`streamWidgetConversationEvents`), and every
 * few seconds while that stream is down, so a person's replies appear.
 */
export const getWidgetConversation = {
    method: 'GET',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/conversations/:conversationId`,
    params: conversationParams,
    query: z.object({}),
    body: z.object({}),
    response: widgetConversationSchema,
};
/**
 * A stream of `text/event-stream` events, not JSON: `changed` whenever the
 * conversation changes (a person's reply, a take-over, an end, a message from
 * another tab), so the widget reads it with `getWidgetConversation` instead of
 * polling. The token goes in `X-Conversation-Token` as for every other route,
 * so it is read with `fetch`, not `EventSource`, and never lands in a URL.
 */
export const streamWidgetConversationEvents = {
    method: 'GET',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/conversations/:conversationId/events`,
    params: conversationParams,
    query: z.object({}),
    body: z.object({}),
    response: z.never(),
};
export const sendVisitorMessageBodySchema = z.object({
    /** The widget's retry key (`patterns/api-conventions.md`, 7). */
    clientMessageId: z.uuid(),
    content: z.string().trim().min(1).max(MAX_VISITOR_MESSAGE_LENGTH),
});
export const sentVisitorMessageSchema = z.object({
    visitorMessage: widgetMessageSchema,
    /**
     * The assistant's answer; `null` once a person has taken the chat over, whose
     * reply comes later (the widget reads it with the conversation).
     */
    answer: widgetMessageSchema.nullable(),
});
/**
 * Needs `X-Conversation-Token`. An ended conversation answers `CONFLICT`, and
 * the widget starts a new one.
 */
export const sendVisitorMessage = {
    method: 'POST',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/conversations/:conversationId/messages`,
    params: conversationParams,
    query: z.object({}),
    body: sendVisitorMessageBodySchema,
    response: sentVisitorMessageSchema,
};
/** Needs `X-Conversation-Token`. Ends the conversation; the next one gets a new id. */
export const endConversation = {
    method: 'POST',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/conversations/:conversationId/end`,
    params: conversationParams,
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
export const reportLeadBodySchema = z.object({ contactType: leadContactTypeSchema });
/** Needs `X-Conversation-Token`. At most one lead per conversation is recorded. */
export const reportLead = {
    method: 'POST',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/conversations/:conversationId/leads`,
    params: conversationParams,
    query: z.object({}),
    body: reportLeadBodySchema,
    response: z.void(),
};
export const submitLeadDetailsBodySchema = leadDetailsSchema;
/**
 * Needs `X-Conversation-Token`. The visitor's details from the lead form, for
 * the business to get back to them. It makes the conversation's lead (or
 * replaces a tap recorded earlier); sending it again replaces the details.
 */
export const submitLeadDetails = {
    method: 'POST',
    path: `${WIDGET_API_BASE}/websites/:websiteKey/conversations/:conversationId/lead-details`,
    params: conversationParams,
    query: z.object({}),
    body: submitLeadDetailsBodySchema,
    response: z.void(),
};
//# sourceMappingURL=v1.js.map